import React, { useEffect, useMemo, useState } from 'react';
import { apiService } from '../../services/api';
import Avatar from '../epics/Avatar';
import JiraKeyChip from '../JiraKeyChip';
import { typeBadge } from '../hygiene/types';
import InfoTip from '../InfoTip';
import FilterChip from '../flow/FilterChip';
import { GapsData, NoTimeData, QuietData } from './types';
import * as fmt from '../../lib/format';

const CARD = 'rounded-2xl bg-white shadow-sm ring-1 ring-gray-200/70 contrast-more:ring-gray-400';
const CAPTION = 'text-[0.6875rem] font-semibold uppercase tracking-wider text-gray-500';
const PAGE = 50;

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

// ─── In progress with no time logged ────────────────────────────────────────────

interface NoTimeProps {
  data: NoTimeData;
  projectKey: string | null;
  onProject: (key: string | null) => void;
}

function NoTimeList({ data, projectKey, onProject }: NoTimeProps) {
  const [includeOlder, setIncludeOlder] = useState(false);
  const [limit, setLimit] = useState(PAGE);

  const rows = useMemo(() => (includeOlder ? data.items : data.items.filter((r) => !r.stale)), [data.items, includeOlder]);
  const shown = rows.slice(0, limit);
  // A project with only stale tickets has nothing to show until the box is ticked, unless it is the one selected.
  const chips = data.by_project.filter((p) => (includeOlder ? p.count : p.recent) > 0 || p.project_key === projectKey).slice(0, 10);

  return (
    <section className={`${CARD} overflow-hidden`} aria-label="In progress with no time logged">
      <div className="p-5">
        <h3 className={CAPTION}>Tickets to ask about</h3>
        <p className="mt-1 text-sm text-gray-600">
          Tickets in progress for {data.threshold_days} {plural(data.threshold_days, 'day', 'days')} or more with no time on them or their sub-tasks. The standard says to ask about these in standup.
        </p>

        {chips.length > 1 && (
          <div className="mt-4 flex flex-wrap gap-2" role="group" aria-label="Filter by project">
            <FilterChip label="All projects" selected={projectKey === null} onClick={() => { onProject(null); setLimit(PAGE); }} />
            {chips.map((p) => (
              <FilterChip
                key={p.project_key}
                label={p.project_key}
                title={p.project_name ?? undefined}
                count={includeOlder ? p.count : p.recent}
                selected={projectKey === p.project_key}
                onClick={() => { onProject(projectKey === p.project_key ? null : p.project_key); setLimit(PAGE); }}
              />
            ))}
          </div>
        )}

        {data.stale > 0 && (
          <label className="mt-4 flex items-start gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={includeOlder}
              onChange={(e) => { setIncludeOlder(e.target.checked); setLimit(PAGE); }}
              className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-200"
            />
            <span>
              Include tickets in progress for more than {data.stale_after_days} days ({fmt.count(data.stale)}).
              <span className="block text-xs text-gray-500">These are usually abandoned or finished without being closed in Jira, so they are hidden by default.</span>
            </span>
          </label>
        )}
      </div>

      {rows.length === 0 ? (
        <div className="border-t border-gray-100 px-5 py-12 text-center text-sm text-gray-500">
          {includeOlder || data.stale === 0
            ? `Every ticket in progress for ${data.threshold_days} ${plural(data.threshold_days, 'day', 'days')} or more${projectKey ? ` in ${projectKey}` : ''} has time logged.`
            : `No ticket younger than ${data.stale_after_days} days${projectKey ? ` in ${projectKey}` : ''} is waiting for time to be logged. Tick the box above to see the older ones.`}
        </div>
      ) : (
        <ul className="divide-y divide-gray-100 border-t border-gray-100">
          {shown.map((r) => (
            <li key={r.issue_key} className="flex items-start justify-between gap-4 px-5 py-3.5">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <JiraKeyChip issueKey={r.issue_key} url={r.jira_url} />
                  {r.issue_type && <span className={`rounded-full px-2 py-0.5 text-[0.6875rem] font-medium ring-1 ring-inset ${typeBadge(r.issue_type)}`}>{r.issue_type}</span>}
                  <span className="text-xs text-gray-500">{r.project_key}</span>
                </div>
                <p className="mt-1 line-clamp-2 text-sm leading-snug text-gray-900" title={r.issue_name ?? undefined}>
                  {r.issue_name || 'Untitled'}
                </p>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-1.5 text-right">
                <span className="text-sm font-medium tabular-nums text-gray-900" title={`In progress since ${fmt.dayMonth(r.since)}`}>
                  {fmt.count(r.days)} {plural(r.days, 'day', 'days')}
                </span>
                <span className="flex items-center gap-1.5 text-xs text-gray-500">
                  {r.assignee ? (
                    <>
                      <Avatar name={r.assignee.name} size="sm" />
                      <span className="max-w-[9rem] truncate">{r.assignee.name}</span>
                    </>
                  ) : (
                    'Unassigned'
                  )}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}

      {rows.length > shown.length && (
        <div className="border-t border-gray-100 p-2">
          <button
            type="button"
            onClick={() => setLimit((n) => n + PAGE)}
            className="w-full rounded-xl px-3 py-2 text-sm font-medium text-blue-700 transition-[background-color,transform] duration-150 hover:bg-blue-50/70 active:scale-[0.99] motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          >
            Show {Math.min(PAGE, rows.length - shown.length)} more <span className="text-gray-500">· {rows.length - shown.length} left</span>
          </button>
        </div>
      )}
    </section>
  );
}

// ─── Went quiet ─────────────────────────────────────────────────────────────────

function QuietList({ data }: { data: QuietData }) {
  return (
    <section className={`${CARD} overflow-hidden`} aria-label="People who went quiet">
      <div className="p-5">
        <h3 className={CAPTION}>People to check in with</h3>
        <p className="mt-1 text-sm text-gray-600">
          People who logged time regularly and then nothing for {data.window_days} days. Some may simply be on leave.
        </p>
      </div>
      {data.items.length === 0 ? (
        <div className="border-t border-gray-100 px-5 py-12 text-center text-sm text-gray-500">
          Everyone who logged regularly has logged something in the last {data.window_days} days.
        </div>
      ) : (
        <ul className="divide-y divide-gray-100 border-t border-gray-100">
          {data.items.map((q) => (
            <li key={q.account_id} className="flex items-center justify-between gap-4 px-5 py-3.5">
              <div className="flex min-w-0 items-center gap-3">
                <Avatar name={q.name} />
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-gray-900">{q.name ?? q.account_id}</div>
                  <div className="text-xs text-gray-500">
                    {fmt.count(q.entries_before)} {plural(q.entries_before, 'entry', 'entries')} · {fmt.hours(q.hours_before)} in the {data.lookback_days} days before
                  </div>
                </div>
              </div>
              <div className="shrink-0 text-right text-xs tabular-nums text-gray-500">
                Last logged
                <div className="mt-0.5 text-sm font-medium text-gray-900">
                  <time dateTime={q.last_logged}>{fmt.dayMonth(q.last_logged)}</time>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ─── The Gaps tab ───────────────────────────────────────────────────────────────

interface Props {
  projectKey: string | null;
  onProject: (key: string | null) => void;
  refreshKey: number;
}

export default function GapsView({ projectKey, onProject, refreshKey }: Props) {
  const [data, setData] = useState<GapsData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiService
      .getEffortGaps({ project_key: projectKey ?? undefined })
      .then((r) => {
        if (cancelled) return;
        if (r.success) {
          setData(r.data);
          setError(null);
        } else setError(r.error || 'Could not load the tickets with no time');
      })
      .catch((e: any) => !cancelled && setError(e?.response?.data?.error || 'Could not load the tickets with no time'));
    return () => {
      cancelled = true;
    };
  }, [projectKey, refreshKey]);

  if (error) return <div className="rounded-xl bg-rose-50 px-4 py-2.5 text-sm text-rose-700 ring-1 ring-rose-200" role="alert">{error}</div>;
  if (!data) {
    return (
      <div className="space-y-5" role="status" aria-label="Loading gaps">
        <div className="grid gap-5 sm:grid-cols-2">
          {[0, 1].map((i) => <div key={i} className={`${CARD} h-28 animate-pulse motion-reduce:animate-none`} />)}
        </div>
        <div className={`${CARD} h-64 animate-pulse motion-reduce:animate-none`} />
      </div>
    );
  }

  const { no_time: noTime, quiet } = data;
  return (
    <div className="space-y-5">
      <div className="grid gap-5 sm:grid-cols-2">
        <section className={`${CARD} p-5`} aria-label="In progress with no time logged, summary">
          <div className="flex items-center">
            <h3 className={CAPTION}>In progress, nothing logged</h3>
            <InfoTip id="effort.no-time" />
          </div>
          <div className="mt-1 text-3xl font-semibold tabular-nums tracking-tight text-gray-900">{fmt.count(noTime.recent)}</div>
          <p className="mt-1 text-sm text-gray-600">{plural(noTime.recent, 'ticket', 'tickets')} to ask about in standup</p>
          {noTime.stale > 0 && (
            <p className="mt-2 text-xs leading-relaxed text-gray-500">
              {fmt.count(noTime.stale)} more {plural(noTime.stale, 'has', 'have')} been in progress for over {noTime.stale_after_days} days. They are usually abandoned or finished without being closed.
            </p>
          )}
        </section>
        <section className={`${CARD} p-5`} aria-label="Went quiet, summary">
          <div className="flex items-center">
            <h3 className={CAPTION}>Went quiet</h3>
            <InfoTip id="effort.quiet" />
          </div>
          <div className="mt-1 text-3xl font-semibold tabular-nums tracking-tight text-gray-900">{fmt.count(quiet.total)}</div>
          <p className="mt-1 text-sm text-gray-600">{plural(quiet.total, 'person', 'people')} with nothing logged for {quiet.window_days} days</p>
          <p className="mt-2 text-xs text-gray-500">Only people who logged regularly in the {quiet.lookback_days} days before are counted.</p>
        </section>
      </div>

      <NoTimeList key={projectKey ?? 'all'} data={noTime} projectKey={projectKey} onProject={onProject} />
      <QuietList data={quiet} />
    </div>
  );
}
